import { useState, useEffect, useRef } from "react";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
// Store
import { useStore } from "../../store";
// API
import {
  createStripeCustomer,
  createStripeSubscription,
} from "../../api/mainApi";
// Helpers
import { extractAddressComponents } from "../../helpers/address";
// Layout
import Header from "../../layout/Header/Header";
// Components
import CheckoutContent from "./CheckoutContent/CheckoutContent";
import ContactUsToast from "../../components/Toast/ContactUsToast/ContactUsToast";
import FullScreenLoader from "../../components/FullScreenLoader/FullScreenLoader";
//Amplitude
import * as amplitude from "@amplitude/analytics-browser";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PK);

const Checkout = () => {
  const navigate = useNavigate();

  const {
    contactInfo,
    scheduleData,
    addressObject,
    stripeData,
    fieldRoutesCustomer,
    setStripeData,
  } = useStore();

  const [isLoading, setIsLoading] = useState(true);
  const isInitializing = useRef(false);

  // Redirect to contact page if no contact info, or address object
  // Redirect to schedule page if no schedule data
  useEffect(() => {
    if (!contactInfo || !addressObject) {
      navigate("/contact");
    }

    if (!scheduleData || !fieldRoutesCustomer) {
      navigate("/schedule");
    }
  }, [addressObject, contactInfo, navigate, scheduleData, fieldRoutesCustomer]);

  useEffect(() => {
    const initializeCheckout = async () => {
      if (isInitializing.current) {
        return;
      }

      if (stripeData) {
        setIsLoading(false);
        return;
      }

      isInitializing.current = true;

      if (!contactInfo || !scheduleData || !addressObject) {
        navigate("/contact");
        return;
      }

      try {
        const customerPayload = {
          email: contactInfo.email,
          name: `${contactInfo.firstName} ${contactInfo.lastName}`,
          phone: contactInfo.mobilePhone,
          address: extractAddressComponents(addressObject),
        };
        const { data: customerData } = await createStripeCustomer(
          customerPayload
        );

        const customerId = customerData.id;
        const subscriptionPayload = {
          customer: customerId,
          metadata: {
            fieldRoutesCustomerId: fieldRoutesCustomer.customerID,
          },
        };
        const { data: subscriptionData } = await createStripeSubscription(
          subscriptionPayload
        );

        if (subscriptionData.latest_invoice.payment_intent.client_secret) {
          setStripeData({
            customerId,
            clientSecret:
              subscriptionData.latest_invoice.payment_intent.client_secret,
            subscriptionId: subscriptionData.id,
          });
          setIsLoading(false);
        }
      } catch (e) {
        const errorEvent = {
          event_type: "Error",
          display_name: "Error",
          event_properties: {
            page_name: "eComm - Checkout",
            error_name: "Failed to store data in Stripe",
            error_source: "Stripe",
          },
        };

        amplitude.track(errorEvent);

        toast(<ContactUsToast />, { theme: "dark" });
      }
    };

    initializeCheckout();
  }, [
    addressObject,
    contactInfo,
    navigate,
    scheduleData,
    setStripeData,
    stripeData,
    fieldRoutesCustomer,
  ]);

  useEffect(() => {
    const pageViewedEvent = {
      event_type: "Page Viewed",
      display_name: "Page Viewed",
      event_properties: {
        page_name: "eComm - Checkout",
      },
    };

    amplitude.track(pageViewedEvent);
  }, []);

  return (
    <main>
      <Header />

      {isLoading && <FullScreenLoader />}

      {stripeData && (
        <Elements
          stripe={stripePromise}
          options={{
            clientSecret: stripeData.clientSecret,
            appearance: {
              theme: "stripe",
              variables: {
                fontFamily: "Manrope, sans-serif",
                colorText: "#292929",
                colorTextPlaceholder: "#a8a8a8",
                colorDanger: "#e54335",
                borderRadius: "10px",
              },
              rules: {
                ".Label": {
                  color: "#292929",
                  fontSize: "12px",
                  fontWeight: "700",
                },
                ".Input": {
                  borderColor: "#a8a8a8",
                  borderRadius: "10px",
                  padding: "10px",
                },
                ".Input:focus": {
                  borderColor: "#00bdee",
                  boxShadow: "none",
                },
                ".Input--invalid": {
                  border: "1px solid #e54335",
                  boxShadow: "none",
                },
              },
            },
          }}
        >
          <CheckoutContent />
        </Elements>
      )}
    </main>
  );
};

export default Checkout;
